import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import type { Node } from '@tiptap/pm/model';
import type { Selection } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import type { TipTapTransactionAdapter } from '../data/TipTapTransactionEventAdapter';

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor,
        renderHTML: (attributes) => {
          return {
            style:
              attributes.backgroundColor &&
              `background-color: ${attributes.backgroundColor}`,
          };
        },
      },
    };
  },
});

const CustomTable = Table.extend({
  addCommands() {
    const parentCommands = this.parent?.() || {};
    return {
      ...parentCommands,
      insertTable: (attrs = {}) => {
        return (props) => {
          if (!parentCommands.insertTable) return false;

          const originalCommand = parentCommands.insertTable(attrs);
          const result = originalCommand(props);
          const adapter: TipTapTransactionAdapter =
            this.editor.storage.transactionEventAdapter;

          if (result && adapter) {
            adapter.createTableEventData({
              name: 'TableAdded',
              locationInDocument: props.tr.selection.from,
              initialColumns: attrs.cols ?? 3,
              initialRows: attrs.rows ?? 3,
            });
          }

          return result;
        };
      },
      deleteTable: () => {
        return (props) => {
          if (!parentCommands.deleteTable) return false;

          const originalCommand = parentCommands.deleteTable();
          const result = originalCommand(props);

          // Note: deleteTable gets tableNode differently, so not using getTableInfo here.
          const tableNode = props.tr.doc.content.firstChild;
          const adapter: TipTapTransactionAdapter =
            props.editor.storage.transactionEventAdapter;

          if (!adapter || !tableNode || tableNode.type.name !== 'table') {
            return result;
          }

          const totalRows = tableNode.childCount ?? 0;
          const totalColumns = getColumnCount(tableNode);

          adapter.createTableEventData({
            name: 'TableRemoved',
            locationInDocument: props.tr.selection.from,
            totalColumns,
            totalRows,
          });

          return result;
        };
      },
      addRowAfter: () => {
        return (props) => {
          if (!parentCommands.addRowAfter) return false;
          const originalCommand = parentCommands.addRowAfter();
          const result = originalCommand(props);

          addRowOrColumn('RowAdded', props.editor, props.tr.selection);

          return result;
        };
      },
      deleteRow: () => {
        return (props) => {
          if (!parentCommands.deleteRow) return false;
          const originalCommand = parentCommands.deleteRow();
          const result = originalCommand(props);

          const adapter: TipTapTransactionAdapter | undefined =
            props.editor.storage.transactionEventAdapter;
          const tableNode = getTableNodeFromSelection(props.tr.selection);

          if (!adapter || !tableNode) {
            return result;
          }

          const totalRows = tableNode.childCount ?? 0;
          const totalColumns = getColumnCount(tableNode);

          adapter.createTableEventData({
            name: 'TableEdited',
            action: 'RowRemoved',
            locationInDocument: props.tr.selection.from,
            totalColumns,
            totalRows,
          });

          return result;
        };
      },
      addColumnAfter: () => {
        return (props) => {
          if (!parentCommands.addColumnAfter) return false;
          const originalCommand = parentCommands.addColumnAfter();
          const result = originalCommand(props);

          addRowOrColumn('ColumnAdded', props.editor, props.tr.selection);

          return result;
        };
      },
      deleteColumn: () => {
        return (props) => {
          if (!parentCommands.deleteColumn) return false;
          const originalCommand = parentCommands.deleteColumn();
          const result = originalCommand(props);

          const adapter: TipTapTransactionAdapter | undefined =
            props.editor.storage.transactionEventAdapter;
          const tableNode = getTableNodeFromSelection(props.tr.selection);

          if (!adapter || !tableNode) {
            return result;
          }

          const totalRows = tableNode.childCount ?? 0;
          const totalColumns = getColumnCount(tableNode);

          adapter.createTableEventData({
            name: 'TableEdited',
            action: 'ColumnRemoved',
            locationInDocument: props.tr.selection.from,
            totalColumns,
            totalRows,
          });

          return result;
        };
      },
      splitCell: () => {
        return (props) => {
          if (!parentCommands.splitCell) return false;
          const originalCommand = parentCommands.splitCell();
          const result = originalCommand(props);

          const adapter: TipTapTransactionAdapter | undefined =
            props.editor.storage.transactionEventAdapter;
          const tableNode = getTableNodeFromSelection(props.tr.selection);

          if (!adapter || !tableNode) {
            return result;
          }

          const totalRows = tableNode.childCount ?? 0;
          const totalColumns = getColumnCount(tableNode);

          adapter.createTableEventData({
            name: 'TableEdited',
            action: 'CellsSplit',
            locationInDocument: props.tr.selection.from,
            totalColumns,
            totalRows,
          });

          return result;
        };
      },
      mergeCells: () => {
        return (props) => {
          if (!parentCommands.mergeCells) return false;
          const originalCommand = parentCommands.mergeCells();
          const result = originalCommand(props);

          const adapter: TipTapTransactionAdapter | undefined =
            props.editor.storage.transactionEventAdapter;
          const tableNode = getTableNodeFromSelection(props.tr.selection);

          if (!adapter || !tableNode) {
            return result;
          }

          const totalRows = tableNode.childCount ?? 0;
          const totalColumns = getColumnCount(tableNode);

          adapter.createTableEventData({
            name: 'TableEdited',
            action: 'CellsMerged',
            locationInDocument: props.tr.selection.from,
            totalColumns,
            totalRows,
          });

          return result;
        };
      },

      setCellAttribute: (attributeName, attributeValue) => {
        return (props) => {
          if (!parentCommands.setCellAttribute) return false;
          const originalCommand = parentCommands.setCellAttribute(
            attributeName,
            attributeValue,
          );
          const result = originalCommand(props);

          if (attributeName === 'backgroundColor') {
            trackBackgroundColorChange(
              attributeValue,
              props.state.selection,
              props.editor,
            );
          }

          return result;
        };
      },
    };
  },
});

const trackBackgroundColorChange = (
  color: string | null,
  selection: Selection,
  editor: Editor,
) => {
  let oldColor: string | null = null;
  let tableCell: Node | null = null;
  // Selection might be in the tableCell's content
  // Walk up the document tree to find the enclosing tableCell
  for (let depth = selection.$from.depth; depth > 0; depth--) {
    const node = selection.$from.node(depth);
    if (node.type.name === 'tableCell') {
      tableCell = node;
      break;
    }
  }
  if (tableCell) {
    oldColor = tableCell.attrs.backgroundColor ?? null;
  }

  const valueHasNotChanged = oldColor === color;
  const valueNotSet = oldColor === null && color === null;
  if (valueNotSet || valueHasNotChanged) {
    return;
  }

  const tableNode = getTableNodeFromSelection(selection);
  const adapter: TipTapTransactionAdapter | undefined =
    editor.storage.transactionEventAdapter;

  if (adapter && tableNode) {
    adapter.createTableEventData({
      name: 'TableEdited',
      action: 'BackgroundColorApplied',
      locationInDocument: selection.from,
      totalColumns: getColumnCount(tableNode),
      totalRows: tableNode?.childCount ?? 0,
    });
  }
};

const addRowOrColumn = (
  action: 'ColumnAdded' | 'RowAdded',
  editor: Editor,
  selection: Selection,
) => {
  const adapter: TipTapTransactionAdapter | undefined =
    editor.storage.transactionEventAdapter;
  const tableNode = getTableNodeFromSelection(selection);

  if (!adapter || !tableNode) {
    return;
  }

  const totalRows = tableNode.childCount ?? 0;
  const totalColumns = getColumnCount(tableNode);

  adapter.createTableEventData({
    name: 'TableEdited',
    action,
    locationInDocument: selection.from,
    totalColumns,
    totalRows,
  });
};

const getColumnCount = (table: Node) =>
  table?.firstChild?.children.reduce(
    (count, cell) => count + cell.attrs.colspan,
    0,
  ) ?? 0;

// Helper function to get adapter and table node
const getTableNodeFromSelection = (selection: Selection) => {
  let tableNode: Node | null = null;
  const { $from } = selection;

  // Iterate upwards from the selection to find the table node
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'table') {
      tableNode = node;
      break;
    }
  }
  return tableNode;
};

export default [CustomTable, TableRow, TableHeader, CustomTableCell];
